// The Mission Control shell (DS §2.2 #1–#6, DC-32, plan D17): D0's navigation items, brand lockup, top bar, signed-in
// person and bottom tab bar around V1's detail pane. It replaces the band shell and keeps every control the walks and
// assistive technology use (reader 7 §3.2): the skip link first; <nav id="nav" aria-label="Main"> with
// aria-current="page"; <main id="main" class="detail"> with the write receipt; the notices in .band__strips; at ≤ 768 px
// a top-bar Menu button opening #nav as a modal drawer with Close menu, and the bottom tab bar.
//
// The frame is his image (UAT plan D-1; SH-1): the navigation runs the full height of column 1 with the brand lockup in
// the top bar's 64 px row, and column 2 stacks the top bar (shell/ShellTopBar.tsx), the notices and the detail pane;
// V1's 6 px column is gone. The nav foot is the person card alone, whose account menu holds Theme (System · Light ·
// Dark), Settings and Sign out (SH-8); the frame holds that menu open, so a navigation closes it and a press outside
// closes it as every D0 menu does (UAT F-69). On a phone the drawer is the whole navigation, with its labels, lockup and
// person card, never the 76 px rail (D-0b), and the bottom tab bar keeps the navigation's order (UAT F-86). A notice
// (view-as, clock offset, session expiry, granted access, acting for) is a D0 Banner, tone warn, announced as a status
// (SH-19). After sign-in the seat's theme (`shell.nav` theme_default) applies while the person has chosen none (SH-9);
// the shell remembers it under its own key, apart from the choice, so index.html's resolver paints it from the next
// load's first frame, and the frame's islands hold motion off until their first paint (UAT F-94).
//
// Two looks, one page (plan D12, reader 7 R1): V2 tokens and component rules apply only inside `.mc`, and V1's palette
// shares their names, so V1 screens must never sit under a `.mc` element. The chrome is therefore `.mc` islands (the
// skip link, the navigation, the top bar, each notice, the tab bar) inside V1's own frame (.app-shell › .shell, whose
// grid V1's stylesheet places), and the detail pane stays outside every island with its V1 look. One McRoot gives the whole frame the
// design system's context (capture flags, hotspots, the portal layer, the settle signal): the frame is portalled from
// inside McRoot into a host beside it, so React context reaches every island and every V2 view while McRoot's own
// `.mc` element holds nothing. That element is the page (mc-page): laid behind the whole frame, it paints the email
// "Background" (--bg) in both themes under the islands, the strips and the detail pane, so one page colour shows
// between the cards on every screen while the detail pane itself stays outside every island. A V2 view registered with
// shell/views.ts is wrapped in its own `.mc` island by App.tsx (shell/island.tsx), whose V1 slot above it holds the V1
// parts such a view still draws.
//
// The items are server data (`shell.nav`): the browser renders what the server sends and holds no role table.
//
// UAT F-146 (Tejesh, 12 Oct 05:54), the layout the person shapes (shell/layout.ts holds the rules and the memory):
// the navigation is resized by the handle on its right edge (260 px by default, 180–320 px) and snaps to the 76 px icon
// rail when dragged below 180 px; the chevron on its edge (and Enter on the handle) folds and opens it, and until the
// person chooses it folds at ≤ 1180 px as DC-38 has it. In the rail every item keeps its name for assistive technology
// and shows it as a tooltip beside the icon; the badges stay on the icons; the person card is the avatar alone. The
// Assistant button beside the bell (and ⌘J / Ctrl+J) opens the assistant panel (shell/AssistantPanel.tsx): an island
// beside V1's frame, docked as a column of .app-shell or, where the detail pane would drop under MAIN_MIN, a sheet over
// it; focus moves into it when it opens and back to the button when it closes from inside. The frame's root carries
// the state for the content (data-aside, --mc-nav-width, --mc-aside-width), and the detail pane is the mc-pane
// container whose width the Split's columns answer (10-surfaces.css). A phone keeps its drawer and tab bar.

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
// The design system by group (its one stylesheet first), not through the root barrel, so the bundle carries the groups
// the shell draws rather than every group's components and words.
import "@/mc/mc.css";
import { McRoot, Tooltip, cx, useCaptureSettle, useHs, useOverflow } from "@/mc/lib";
import { NAV_ITEM_LABELS, type NavKey } from "@/mc/labels";
import { Banner, Button, Icon, IconButton, Pill, type IconName, type MenuItem } from "@/mc/core";
import { BrandLockup, NavItem, SHELL_LABELS, SignedInPerson } from "@/mc/g4a";
import { CAPABILITIES } from "@/views/Access";
import { useQueryClient } from "@tanstack/react-query";
import { clockNow, freezeClock, lastActivityAt, setBusinessTimeZone } from "@/api/runtime";
import { accessList, adminSystem, adminViewAsStop, delegationList, getMe, type NavItemOut, type ShellNavOutput } from "@/api/tools";
import { errorText, useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { confirmNavigation, useRoute } from "@/router";
import { fmtDate, fmtTime, focusable } from "@/ui";
import { WriteReceipt } from "./WriteReceipt";
import { activeItem, useShellNav } from "./nav";
import { closeOnHashchange, ShellTopBar } from "./ShellTopBar";
import { chooseTheme, themePreference, type ThemeChoice } from "./theme";
import { isCapture } from "./capture";
import { AssistantPanel, ASSISTANT_ID } from "./AssistantPanel";
import { ResizeHandle } from "./ResizeHandle";
import {
  ASSISTANT_KEYS, asideDrag, asideKey, frameLayout, isAssistantShortcut, MOBILE_MAX, NAV, navDrag, navKey, navToggle,
  readShellLayout, shortcutHint, writeShellLayout, type ShellLayout,
} from "./layout";
/** The V2 top bar's height (--topbar-h); V1's drawer and scrim hang from --band-h. */
const TOP_BAR_PX = 64;
/** A desktop or rail navigation fills its island, its list scrolling inside it; the phone's drawer places itself. */
const NAV_FILL: CSSProperties = { height: "100%", overflow: "auto" };

/** The three theme choices in his words; the account menu's Theme item names the one in force. */
const THEME_WORD: Readonly<Record<ThemeChoice, string>> = { system: "System", light: "Light", dark: "Dark" };
/** The order Theme steps through: the dark page first, then light, then the device's own. */
const THEME_STEPS: readonly ThemeChoice[] = ["dark", "light", "system"];

/** The choice the account menu's Theme item moves to from `choice`. */
export function nextTheme(choice: ThemeChoice): ThemeChoice {
  return THEME_STEPS[(THEME_STEPS.indexOf(choice) + 1) % THEME_STEPS.length]!;
}

/** The account menu (SH-8): Theme (me.theme) naming the choice in force, Settings, and Sign out (me.sign-out). */
export function accountItems({ theme, onTheme, onSignOut }: {
  theme: ThemeChoice;
  onTheme: (choice: ThemeChoice) => void;
  onSignOut: () => void;
}): MenuItem[] {
  return [
    { label: `Theme · ${THEME_WORD[theme]}`, icon: "eye", onSelect: () => onTheme(nextTheme(theme)), hs: "me.theme" },
    { label: NAV_ITEM_LABELS.settings, href: "#settings", icon: "settings", hs: "me.menu.settings" },
    { label: "Sign out", icon: "back", onSelect: onSignOut, hs: "me.sign-out" },
  ];
}

/** The stored theme choice (index.html's resolver keeps it under ta.theme), or null where storage is unavailable. */
function storedTheme(): string | null {
  try {
    return localStorage.getItem("ta.theme");
  } catch {
    return null;
  }
}

/**
 * The seat's theme after sign-in (SH-9: `shell.nav` theme_default, light only for an Interviewer alone): it applies
 * while the person has chosen none. A stored choice wins (an unknown stored value is none, as the resolver reads it),
 * and a photographed frame keeps the theme its address pins. Returns the theme to apply, or null.
 */
export function seatTheme(stored: string | null, seatDefault: "dark" | "light" | undefined, capture: boolean): "dark" | "light" | null {
  if (capture || !seatDefault) return null;
  if (stored !== null && (THEME_STEPS as readonly string[]).includes(stored)) return null;
  return seatDefault;
}

/**
 * The key the seat's theme is remembered under, apart from the person's choice (`ta.theme`): index.html's resolver
 * paints it before the first frame while no choice is stored (UAT F-94). It is never a choice, and a choice always wins.
 */
export const SEAT_THEME_KEY = "ta.theme.seat";

/** Remembers the seat's theme for the next load's first paint, or forgets it (null) when the shell is left. */
export function rememberSeatTheme(theme: "dark" | "light" | null, storage?: Pick<Storage, "setItem" | "removeItem">): void {
  try {
    const store = storage ?? localStorage;
    if (theme) store.setItem(SEAT_THEME_KEY, theme);
    else store.removeItem(SEAT_THEME_KEY);
  } catch {
    // Storage can be unavailable; the seat's theme still applies after sign-in.
  }
}

/** Calls `done` after the next two animation frames, once the frame drawn now has been painted; returns the cancel. */
export function afterFirstPaint(
  done: () => void,
  frame: (callback: () => void) => number = requestAnimationFrame,
  cancel: (id: number) => void = cancelAnimationFrame,
): () => void {
  let second: number | undefined;
  const first = frame(() => { second = frame(done); });
  return () => {
    cancel(first);
    if (second !== undefined) cancel(second);
  };
}

/** The page's theme for a choice, as the resolver applies it. */
function resolvedTheme(choice: ThemeChoice): "dark" | "light" {
  if (choice !== "system") return choice;
  return typeof window.matchMedia === "function" && !window.matchMedia("(prefers-color-scheme: dark)").matches ? "light" : "dark";
}

/** Class names on the frame's own elements, where V1's frame classes sit beside D0's (cx admits mc-* alone). */
function frameClasses(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

/** A notice in the frame (SH-19): a D0 Banner, tone warn, in its own `.mc` island, announced as a status. */
export function ShellNotice({ children, right, live, className }: {
  children: ReactNode;
  right?: ReactNode | undefined;
  live?: boolean | undefined;
  className?: string | undefined;
}) {
  return (
    <div className={frameClasses("mc", "mc-shell-notice", className)} role="status" aria-live={live ? "polite" : undefined}>
      <Banner tone="warn" right={right}>{children}</Banner>
    </div>
  );
}

/** The design system's key for a server nav key ("action-center" → "actionCenter"), when D0 draws that destination. */
function navKeyOf(item: NavItemOut): NavKey | undefined {
  const key = item.key.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
  return key in NAV_ITEM_LABELS && NAV_ITEM_LABELS[key as NavKey] === item.label ? (key as NavKey) : undefined;
}

/**
 * The bottom tab bar's items (UAT F-86): the navigation's own items whose keys `shell.nav` puts in the bar, in the
 * navigation's order, so the rail and the bar read alike (the keys pick the items, they do not order them).
 */
export function tabbarItems(items: readonly NavItemOut[], keys: readonly string[]): NavItemOut[] {
  return items.filter((item) => keys.includes(item.key));
}

/** The icon of a transitional destination D0 does not draw (Requests, Intake & Panel). */
const TRANSITIONAL_ICON: Readonly<Record<string, IconName>> = { requests: "file", intake: "list" };

/**
 * One destination. A destination of the DC-32 master set is D0's own NavItem (its icon, its words, its badge); a
 * transitional one the server still lists (Requests until B2, Intake & Panel until B4) is drawn with the same classes
 * and the server's words, so every item reads and behaves alike.
 */
function ShellItem({ item, active, bar, rail, hs, onNavigate }: {
  item: NavItemOut;
  active: boolean;
  bar?: boolean;
  /** In the icon rail (UAT F-146): the name is the item's tooltip beside it. */
  rail?: boolean;
  hs: string;
  onNavigate: () => void;
}) {
  const key = navKeyOf(item);
  const hot = useHs(hs);
  const { ref, overflows } = useOverflow<HTMLSpanElement>();
  if (key) {
    return <NavItem item={key} active={active} href={item.route} badge={item.badge ?? undefined} onNavigate={onNavigate} hs={hs} bar={bar} tooltip={rail} />;
  }
  const badge = item.badge ?? 0;
  const link = (
    <a className={cx(bar ? "mc-tbar-i" : "mc-nav-item", active && "mc-on")} href={item.route} aria-current={active ? "page" : undefined} onClick={onNavigate} {...hot}>
      <Icon name={TRANSITIONAL_ICON[item.key] ?? "list"} size={bar ? 18 : 20} />
      <span ref={ref} className="mc-nav-t" title={overflows && !rail ? item.label : undefined}>{item.label}</span>
      {badge > 0 ? <span className="mc-nb"><span className="mc-sr">, </span>{badge}</span> : null}
    </a>
  );
  return rail ? <Tooltip label={item.label} mode="mirror" placement="end">{link}</Tooltip> : link;
}

export interface MissionShellProps {
  children: ReactNode;
}

/**
 * The shell: one McRoot whose context reaches the portalled frame and whose own element is the page behind it (see the
 * head of this file). Under capture the clock is the server's business instant, frozen once, and McRoot's relative
 * times read the same instant.
 */
export function MissionShell({ children }: MissionShellProps) {
  const { me } = useSession();
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [capture] = useState(isCapture);
  const [now] = useState(() => {
    if (capture) freezeClock(me?.business_now);
    return capture ? clockNow() : undefined;
  });
  return (
    <>
      <div ref={setHost} style={{ display: "contents" }} />
      <McRoot now={now} capture={capture} className="mc-page">{host ? createPortal(<Frame capture={capture}>{children}</Frame>, host) : null}</McRoot>
    </>
  );
}

/**
 * The capture settle signal waits for the screen, not only the chrome (DS §5, reader 7 §2.7): on every route the
 * shell registers one pending item with McRoot's settle signal, released once a visible `main h1` is there, no
 * skeleton or busy region is left in the detail pane and no read is in flight. Polling is off under capture, so the
 * reads do settle. Outside capture nothing is registered or observed.
 */
function useArrivalSettle(routeKey: string, capture: boolean): void {
  const settle = useCaptureSettle();
  const queries = useQueryClient();
  useEffect(() => {
    if (!capture) return; // only a photographed page waits for its screen; McRoot's own signal serves the rest
    let release: () => void = () => undefined;
    settle.pending(new Promise<void>((resolve) => { release = resolve; }));
    let frame = 0;
    const check = () => {
      const heading = Array.from(document.querySelectorAll<HTMLElement>("main h1")).some((node) => node.getClientRects().length > 0);
      const busy = document.querySelector("main .skeleton, main .skeleton__cell, main .mc-skel, main [aria-busy='true']");
      if (heading && !busy && queries.isFetching() === 0) release();
    };
    const soon = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(check); };
    const observer = new MutationObserver(soon);
    const root = document.getElementById("root");
    if (root) observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["aria-busy", "class"] });
    const unsubscribe = queries.getQueryCache().subscribe(soon);
    soon();
    return () => { observer.disconnect(); unsubscribe(); cancelAnimationFrame(frame); release(); };
  }, [routeKey, capture, settle, queries]);
}

function Frame({ children, capture }: { children: ReactNode; capture: boolean }) {
  const frame = useRef<HTMLDivElement>(null);
  const strips = useRef<HTMLDivElement>(null);
  const detail = useRef<HTMLElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const navScope = useRef<HTMLDivElement>(null);
  const navIsland = useRef<HTMLDivElement>(null);
  const assistantButton = useRef<HTMLButtonElement>(null);
  const asidePanel = useRef<HTMLElement>(null);
  const asideHeading = useRef<HTMLHeadingElement>(null);
  const { me, can, signOut } = useSession();
  const route = useRoute();
  const navQuery = useShellNav({ poll: !capture });
  useArrivalSettle(route.raw, capture);
  const nav: ShellNavOutput | undefined = navQuery.data;

  // Every timestamp on every screen is read in the business calendar's zone: set in the shell's own render, before
  // any screen under it formats a date, once per zone.
  const zone = useRef<string | null>(null);
  if (me?.timezone && zone.current !== me.timezone) {
    zone.current = me.timezone;
    setBusinessTimeZone(me.timezone);
  }

  // §1.1's KPI tiles read the detail pane's width; the notices' height keeps a focused title clear of them.
  useLayoutEffect(() => {
    const pane = detail.current;
    if (!pane) return;
    const measure = () => {
      const width = pane.getBoundingClientRect().width;
      pane.style.setProperty("--kpi-columns", String(width < 640 ? 1 : width < 1180 ? 2 : 4));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(pane);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => {
    const notices = strips.current;
    if (!notices) return;
    const measure = () => frame.current?.style.setProperty("--notice-height", `${notices.getBoundingClientRect().height}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(notices);
    return () => observer.disconnect();
  }, []);

  const [theme, setTheme] = useState(themePreference);
  useEffect(() => {
    const sync = () => setTheme(themePreference());
    window.addEventListener("ta:theme", sync);
    return () => window.removeEventListener("ta:theme", sync);
  }, []);
  // SH-9: with no choice of the person's own, the seat's theme is applied (never stored, so it is never mistaken for
  // a choice); a choice made from the account menu ends it, and leaving the shell hands the page back to the resolver.
  const chose = useRef(false);
  const seat = chose.current ? null : seatTheme(storedTheme(), nav?.theme_default, capture);
  useEffect(() => {
    if (!seat) return;
    const root = document.documentElement;
    root.dataset.theme = seat;
    return () => {
      if (!chose.current && root.dataset.theme === seat) root.dataset.theme = resolvedTheme(themePreference());
    };
  }, [seat]);
  const pickTheme = (choice: ThemeChoice) => {
    chose.current = true;
    chooseTheme(choice);
  };
  // UAT F-94: the seat's theme is kept for the next load's first paint (outside a photographed frame), and forgotten
  // when the shell is left, so the sign-in after it opens dark.
  const seatDefault = nav?.theme_default;
  useEffect(() => {
    if (!capture && seatDefault) rememberSeatTheme(seatDefault);
  }, [capture, seatDefault]);
  useEffect(() => (capture ? undefined : () => rememberSeatTheme(null)), [capture]);
  // The islands hold motion off (data-boot) until shell.nav has answered, so the seat's theme is applied, and that
  // frame has been painted: the theme the page opens in is painted, never animated in.
  const [booting, setBooting] = useState(true);
  const settled = capture || navQuery.data !== undefined || navQuery.isError;
  useEffect(() => (booting && settled ? afterFirstPaint(() => setBooting(false)) : undefined), [booting, settled]);
  const boot = booting ? "" : undefined;

  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const on = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const mobile = viewportWidth <= MOBILE_MAX;

  // UAT F-146: the navigation's width and fold and the assistant panel, remembered per browser (shell/layout.ts).
  const [layout, setLayout] = useState<ShellLayout>(() => readShellLayout(capture));
  useEffect(() => { writeShellLayout(layout, capture); }, [layout, capture]);
  const fx = frameLayout(layout, viewportWidth);
  const rail = fx.navFolded;
  const asideOpen = fx.aside !== "closed";
  const [resizing, setResizing] = useState(false);
  const [hint] = useState(() => shortcutHint(typeof navigator === "undefined" ? undefined : navigator.platform));
  // Focus moves into the panel when the person opens it (never on a load that restores it open) …
  const focusPanel = useRef(false);
  useEffect(() => {
    if (!asideOpen || !focusPanel.current) return;
    focusPanel.current = false;
    asideHeading.current?.focus();
  }, [asideOpen]);
  const openAside = () => {
    focusPanel.current = true;
    setLayout((now) => ({ ...now, aside: { ...now.aside, open: true } }));
  };
  // … and back to the Assistant button when it closes from inside it.
  const closeAside = () => {
    if (asidePanel.current?.contains(document.activeElement)) assistantButton.current?.focus();
    setLayout((now) => ({ ...now, aside: { ...now.aside, open: false } }));
  };
  const toggleAside = () => (layout.aside.open ? closeAside() : openAside());
  const toggleRef = useRef(toggleAside);
  toggleRef.current = toggleAside;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isAssistantShortcut(event)) return;
      // A modal dialog owns the keyboard until it closes.
      if (document.activeElement?.closest('[aria-modal="true"]')) return;
      event.preventDefault();
      toggleRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const navFrom = () => navIsland.current?.getBoundingClientRect().left ?? 0;
  const asideEdge = () => asidePanel.current?.getBoundingClientRect().right ?? window.innerWidth;

  const [drawer, setDrawer] = useState(false);
  const drawerOpen = mobile && drawer;
  useEffect(() => { if (!mobile) setDrawer(false); }, [mobile]);
  useEffect(() => { setDrawer(false); }, [route.raw]); // a navigation closes the drawer
  const [account, setAccount] = useState(false);
  useEffect(() => (account ? closeOnHashchange(window, () => setAccount(false)) : undefined), [account]);
  useEffect(() => {
    if (!drawerOpen) return;
    const opener = menuButton.current;
    const openedRoute = window.location.hash;
    focusable(navScope.current)[0]?.focus();
    function onKey(event: KeyboardEvent) {
      // A confirmation dialog opened from the drawer owns its own focus scope.
      if (document.activeElement?.closest('[role="dialog"]') !== navScope.current) return;
      if (event.key === "Escape") {
        event.preventDefault(); event.stopPropagation(); setDrawer(false);
      } else if (event.key === "Tab") {
        const elements = focusable(navScope.current), first = elements[0], last = elements.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener?.isConnected && window.location.hash === openedRoute) opener.focus();
    };
  }, [drawerOpen]);

  // §2 session expiry: two minutes before the idle window closes, a non-modal strip offers "Stay signed in".
  const idleMs = (me?.session_idle_minutes ?? 30) * 60_000;
  const [idleLeftMs, setIdleLeftMs] = useState<number | null>(null);
  useEffect(() => {
    if (capture) return; // a photographed frame never shows the idle strip
    const id = window.setInterval(() => {
      const left = idleMs - (Date.now() - lastActivityAt);
      if (left <= 0) {
        setIdleLeftMs(0);
        // a 401 here hands the app to the sign-in page with the return route kept
        void getMe().then((m) => setBusinessTimeZone(m.timezone)).catch(() => undefined);
      } else setIdleLeftMs(left <= 2 * 60_000 ? left : null);
    }, 5_000);
    return () => window.clearInterval(id);
  }, [idleMs, capture]);

  const lens = me?.view_as as ({ person_id: string; name: string; started_at?: string; expires_at?: string } | null | undefined);
  const [lensNow, setLensNow] = useState(clockNow);
  useEffect(() => {
    if (capture) return;
    const id = window.setInterval(() => setLensNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [capture]);
  const stop = useWrite(adminViewAsStop);
  const lensLeft = lens?.expires_at ? Math.max(0, Math.ceil((Date.parse(lens.expires_at) - lensNow) / 1000)) : null;
  const system = useRead(["system"], () => adminSystem({}), { enabled: can("admin.system") && !lens, refetchInterval: capture ? false : 15_000 });
  const offsetDays = system.data ? Math.round((Date.parse(system.data.business_now) - system.dataUpdatedAt) / 86_400_000) : 0;

  const items = nav?.items ?? [];
  const current = activeItem(items, route);
  const close = () => setDrawer(false);
  const signOutNow = () => confirmNavigation(() => void signOut());
  const inert = (on: boolean) => (on ? { inert: "" } : {});
  const person = nav?.person;
  const tabbar = tabbarItems(items, nav?.tabbar ?? []);
  const shellStyle = { "--nav-w": `${fx.navWidth}px` } as CSSProperties;
  // The state the content may answer (UAT F-146, ahead of F-145's grid): whether the assistant panel is open and how it
  // lies, and the widths the navigation and the panel take.
  const rootStyle = {
    "--band-h": `${TOP_BAR_PX}px`,
    "--mc-nav-width": `${fx.navWidth}px`,
    "--mc-aside-width": `${fx.asideWidth}px`,
  } as CSSProperties;

  return (
    <div
      className="app-shell"
      ref={frame}
      style={rootStyle}
      data-aside={asideOpen ? "open" : "closed"}
      data-aside-as={asideOpen ? fx.aside : undefined}
      data-resizing={resizing ? "" : undefined}
    >
      <div className="mc mc-skipwrap">
        <a className="mc-skip" href="#main" data-skip-link onClick={(event: MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          const main = document.getElementById("main");
          const target = main?.querySelector<HTMLElement>("h1") ?? main;
          if (target) { target.tabIndex = -1; target.focus(); target.scrollIntoView({ block: "start" }); }
        }}>Skip to main content</a>
      </div>
      <div className={frameClasses("shell", mobile && "shell--narrow")} style={shellStyle}>
        {drawerOpen && <div className="scrim" onClick={close} aria-hidden="true" />}
        <div className="nav-scope" ref={navScope} role={drawerOpen ? "dialog" : undefined} aria-modal={drawerOpen ? true : undefined} aria-label={drawerOpen ? "Main menu" : undefined}>
          <div className="mc shell__nav" data-boot={boot} ref={navIsland} style={mobile ? undefined : { "--nav-w": `${fx.navWidth}px` } as CSSProperties}>
            <nav
              id="nav"
              className={frameClasses("mc-nav", mobile && "nav--drawer", mobile && "mc-nav-drawer", drawerOpen && "nav--open")}
              aria-label="Main"
              aria-hidden={mobile && !drawer ? true : undefined}
              data-fold={mobile ? undefined : rail ? "rail" : "open"}
              style={mobile ? undefined : NAV_FILL}
              {...inert(mobile && !drawer)}
            >
              {mobile ? <IconButton icon="x" label="Close menu" onClick={close}  /> : null}
              <BrandLockup />
              <ul className="mc-nav-list">
                {items.map((item) => (
                  <li key={item.key}>
                    <ShellItem item={item} active={item === current} rail={!mobile && rail}  onNavigate={close} />
                  </li>
                ))}
              </ul>
              <div className="mc-nav-foot">
                {person && me ? (
                  <SignedInPerson
                    name={person.name}
                    seat={person.role_words.join(" · ")}
                    seatKey={person.seat_key}
                    hueKey={lens?.person_id ?? me.person_id}
                    items={accountItems({ theme: seat ?? theme, onTheme: pickTheme, onSignOut: signOutNow })}
                    open={account}
                    onOpenChange={setAccount}
                    
                  />
                ) : null}
              </div>
            </nav>
            {mobile ? null : (
              <ResizeHandle
                label={SHELL_LABELS.navigation}
                controls="nav"
                value={fx.navWidth}
                min={NAV.rail}
                max={NAV.max}
                edge="end"
                onDrag={(clientX) => setLayout((now) => ({ ...now, nav: navDrag(now.nav, clientX - navFrom()) }))}
                onKey={(key) => {
                  const next = navKey(layout.nav, key, viewportWidth);
                  if (next) setLayout((now) => ({ ...now, nav: next }));
                  return next !== null;
                }}
                onDragChange={setResizing}
                
              />
            )}
            {mobile ? null : (
              <div className="mc-navtog">
                <IconButton
                  icon="right"
                  sm
                  label={SHELL_LABELS.navigation}
                  aria-expanded={!rail}
                  aria-controls="nav"
                  onClick={() => setLayout((now) => ({ ...now, nav: navToggle(now.nav, viewportWidth) }))}
                  
                />
              </div>
            )}
          </div>
        </div>
        <div className="mc shell__top" data-boot={boot} {...inert(drawerOpen)}>
          <ShellTopBar
            nav={nav}
            route={route}
            lead={mobile ? (
              <IconButton ref={menuButton} icon="list" label="Menu" aria-expanded={drawerOpen} aria-controls="nav" onClick={() => setDrawer((open) => !open)}  />
            ) : null}
            assistant={{
              open: asideOpen,
              onToggle: toggleAside,
              controls: ASSISTANT_ID,
              keys: ASSISTANT_KEYS,
              hint,
              buttonRef: assistantButton,
            }}
          />
        </div>
        <div className="band__strips" ref={strips} {...inert(drawerOpen)}>
          {lens && (
            <ShellNotice
              className="admin-lens"
              right={<Button sm busy={stop.isPending} onClick={() => stop.mutate({}, { onSuccess: () => { window.location.hash = "#admin/access"; window.location.reload(); } })}>Stop viewing</Button>}
            >
              {lensLeft === 0 ? "View-as ended; start it again." : `Viewing as ${lens.name || lens.person_id} · ${me?.role_labels.join(" · ")} · read-only · started ${fmtTime(lens.started_at)}`}{lensLeft !== null && lensLeft > 0 ? ` · ${Math.floor(lensLeft / 60)}:${String(lensLeft % 60).padStart(2, "0")} remaining` : ""}
              {stop.error ? <span className="mc-shell-notice-err">{errorText(stop.error)}</span> : null}
            </ShellNotice>
          )}
          {offsetDays !== 0 && <ShellNotice>Clock offset: {offsetDays > 0 ? "+" : ""}{offsetDays} days</ShellNotice>}
          {idleLeftMs !== null && (
            <ShellNotice
              live
              right={idleLeftMs > 0 ? (
                <Button sm onClick={() => void getMe().then((m) => { setBusinessTimeZone(m.timezone); setIdleLeftMs(null); }).catch(() => undefined)}>
                  Stay signed in
                </Button>
              ) : undefined}
            >
              {idleLeftMs > 0 ? `Your session ends in ${Math.ceil(idleLeftMs / 60_000)} minute${idleLeftMs > 60_000 ? "s" : ""}.` : "Your session has ended."}
            </ShellNotice>
          )}
          <StandingStrips />
        </div>
        <main
          ref={detail}
          className="detail"
          id="main"
          tabIndex={-1}
          style={mobile ? { paddingBottom: 72 } : undefined}
          {...inert(drawerOpen)}
        >
          {children}
          <WriteReceipt />
        </main>
        {mobile && tabbar.length > 0 ? (
          <div className="mc shell__tabbar" data-boot={boot} {...inert(drawerOpen)}>
            <nav className="mc-tabbar" aria-label={SHELL_LABELS.navigation}>
              <ul>
                {tabbar.map((item) => (
                  <li key={item.key}>
                    <ShellItem bar item={item} active={item === current}  onNavigate={close} />
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        ) : null}
      </div>
      <AssistantPanel
        open={asideOpen}
        sheet={fx.aside === "sheet"}
        width={fx.asideWidth}
        max={fx.asideMax}
        resizable={!mobile}
        panelRef={asidePanel}
        headingRef={asideHeading}
        onClose={closeAside}
        onDrag={(clientX) => setLayout((now) => ({ ...now, aside: asideDrag(now.aside, Math.min(asideEdge() - clientX, fx.asideMax)) }))}
        onKey={(key) => {
          const next = asideKey(layout.aside, key);
          if (next) setLayout((now) => ({ ...now, aside: { ...next, width: Math.min(next.width, fx.asideMax) } }));
          return next !== null;
        }}
        onDragChange={setResizing}
        inert={drawerOpen}
      />
    </div>
  );
}

/**
 * The notices the frame carries while somebody is working on a requisition that is not simply theirs
 * (V1-INTERACTIONS §4.19 and §4.20), read from what the server returns for THIS requisition. Nothing is inferred.
 */
function StandingStrips() {
  const { me, can } = useSession();
  const route = useRoute();
  const rid = route.view === "requisitions" ? route.parts[0] ?? "" : "";
  const onRequisition = /^R-\d+$/.test(rid);
  const access = useRead(["access", rid], () => accessList({ requisition_id: rid }), { enabled: onRequisition && can("access.list"), retry: false, staleTime: 30_000 });
  const dele = useRead(["delegations", "mine"], () => delegationList({}), { enabled: onRequisition && can("delegation.list"), retry: false, staleTime: 30_000 });
  if (!onRequisition) return null;
  const mine = (access.data?.mine ?? {}) as { scope?: string | null; grant_id?: string | null; until?: string | null };
  const grant = access.data?.grants.find((g) => g.id === mine.grant_id);
  const acting = (dele.data?.delegations ?? []).filter((d) => d.live && d.status === "active" && d.grantee_id === me?.person_id && (d.requisition_id === rid || d.requisition_id === null));
  return (
    <>
      {grant && mine.until && (
        <ShellNotice>
          Access to {rid} granted by {grant.granted_by_name} until {fmtDate(mine.until)} · {grant.scope === "read" ? "read access" : "cover access"}
        </ShellNotice>
      )}
      {acting.map((d) => (
        <ShellNotice key={d.id}>
          Acting for {d.grantor_name} on {rid} · {CAPABILITIES.filter(capability => d.capabilities.includes(capability.key)).map(capability => (
            <span key={capability.key} className="mc-shell-cap" title={capability.help}><Pill dot={false}>{capability.label}</Pill></span>
          ))}
        </ShellNotice>
      ))}
    </>
  );
}
